<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data?.general || {});

// Form sections separated for independent tracking
const tutorials = reactive({
  withdrawVideoLink: '',
  withdrawTutorialLink: '',
  rechargeVideoLink: '',
  rechargeTutorialLink: ''
});

const assistance = reactive({
  whatsappComplaintLink: '',
  whatsappAssistanceLink: '',
  telegramAssistanceLink: '',
  email: ''
});

const otherLinks = reactive({
  snapchatLink: '',
  passwordResetUrl: '',
  affiliationDetailText: ''
});

const downloads = reactive({
  netflixDownloadUrl: '',
  crunchyrolDownloadUrl: '',
  xbetDownloadUrl: '',
  betwinnerDownloadUrl: '',
  oneWinDownloadUrl: '',
  primeVideoDownloadUrl: '',
  spotifyDownloadUrl: '',
  nordVPNDownloadUrl: '',
  privateVPNDownloadUrl: '',
  cyberGhostDownloadUrl: ''
});

const minAmounts = reactive({
  minAmountForTransaction: 0,
  minAmountFundWallet: 0,
  minAmountwithdrawalWallet: 0,
  minAmountBuyXbet: 0,
  minAmountSellXbet: 0,
  minAmountBuyBetwinner: 0,
  minAmountSellBetwinner: 0,
  minAmountBuyOneWin: 0,
  minAmountSellOneWin: 0,
  minAmountBuyPm: 0,
  minAmountSellPm: 0,
  minAmountBuyUsdt: 0,
  minAmountSellUsdt: 0,
  minAmountBuyEightStarz: 0,
  minAmountSellEightStarz: 0,
  minAmountBuyMelbet: 0,
  minAmountSellMelbet: 0
});

const currency = reactive({
  userCurrency: ''
});

// Initialize all sections
watch(
  sourceData,
  (data) => {
    if (!data) return;

    Object.assign(tutorials, {
      withdrawVideoLink: data.withdrawVideoLink || '',
      withdrawTutorialLink: data.withdrawTutorialLink || '',
      rechargeVideoLink: data.rechargeVideoLink || '',
      rechargeTutorialLink: data.rechargeTutorialLink || ''
    });

    Object.assign(assistance, {
      whatsappComplaintLink: data.whatsappComplaintLink || '',
      whatsappAssistanceLink: data.whatsappAssistanceLink || '',
      telegramAssistanceLink: data.telegramAssistanceLink || '',
      email: data.email || ''
    });

    Object.assign(otherLinks, {
      snapchatLink: data.snapchatLink || '',
      passwordResetUrl: data.passwordResetUrl || '',
      affiliationDetailText: data.affiliationDetailText || ''
    });

    Object.assign(downloads, {
      netflixDownloadUrl: data.netflixDownloadUrl || '',
      crunchyrolDownloadUrl: data.crunchyrolDownloadUrl || '',
      xbetDownloadUrl: data.xbetDownloadUrl || '',
      betwinnerDownloadUrl: data.betwinnerDownloadUrl || '',
      oneWinDownloadUrl: data.oneWinDownloadUrl || '',
      primeVideoDownloadUrl: data.primeVideoDownloadUrl || '',
      spotifyDownloadUrl: data.spotifyDownloadUrl || '',
      nordVPNDownloadUrl: data.nordVPNDownloadUrl || '',
      privateVPNDownloadUrl: data.privateVPNDownloadUrl || '',
      cyberGhostDownloadUrl: data.cyberGhostDownloadUrl || ''
    });

    Object.assign(minAmounts, {
      minAmountForTransaction: data.minAmountForTransaction || 0,
      minAmountFundWallet: data.minAmountFundWallet || 0,
      minAmountwithdrawalWallet: data.minAmountwithdrawalWallet || 0,
      minAmountBuyXbet: data.minAmountBuyXbet || 0,
      minAmountSellXbet: data.minAmountSellXbet || 0,
      minAmountBuyBetwinner: data.minAmountBuyBetwinner || 0,
      minAmountSellBetwinner: data.minAmountSellBetwinner || 0,
      minAmountBuyOneWin: data.minAmountBuyOneWin || 0,
      minAmountSellOneWin: data.minAmountSellOneWin || 0,
      minAmountBuyPm: data.minAmountBuyPm || 0,
      minAmountSellPm: data.minAmountSellPm || 0,
      minAmountBuyUsdt: data.minAmountBuyUsdt || 0,
      minAmountSellUsdt: data.minAmountSellUsdt || 0,
      minAmountBuyEightStarz: data.minAmountBuyEightStarz || 0,
      minAmountSellEightStarz: data.minAmountSellEightStarz || 0,
      minAmountBuyMelbet: data.minAmountBuyMelbet || 0,
      minAmountSellMelbet: data.minAmountSellMelbet || 0
    });

    Object.assign(currency, {
      userCurrency: data.userCurrency || ''
    });
  },
  { immediate: true }
);

// Check if section has changes
const sectionChanged = (section) =>
  Object.keys(section).some((key) => section[key] !== sourceData.value?.[key]);

const hasChanges = computed(
  () =>
    sectionChanged(tutorials) ||
    sectionChanged(assistance) ||
    sectionChanged(otherLinks) ||
    sectionChanged(downloads) ||
    sectionChanged(minAmounts) ||
    sectionChanged(currency)
);

function save() {
  if (!hasChanges.value) return;

  const payload = { general: {} };
  const allSections = {
    tutorials,
    assistance,
    otherLinks,
    downloads,
    minAmounts,
    currency
  };

  Object.values(allSections).forEach((section) => {
    Object.keys(section).forEach((key) => {
      if (section[key] !== sourceData.value?.[key]) {
        payload.general[key] = section[key];
      }
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_GENERAL, payload);
}

function resetSection(section) {
  Object.keys(section).forEach((key) => {
    section[key] =
      sourceData.value?.[key] || (typeof section[key] === 'number' ? 0 : '');
  });
}

function resetAll() {
  resetSection(tutorials);
  resetSection(assistance);
  resetSection(otherLinks);
  resetSection(downloads);
  resetSection(minAmounts);
  resetSection(currency);
}
</script>

<template>
  <div class=" space-y-8">
    <!-- HEADER -->
    <div class="flex justify-between items-center sticky top-0 py-2 z-2">
      <h2 class="text-lg font-semibold">Paramètres généraux</h2>
      <div class="flex gap-4" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="resetAll"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- TUTORIALS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Tutoriels</h3>
        <button
          v-if="sectionChanged(tutorials)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(tutorials)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Vidéo retrait</label>
          <input class="inputComponent" v-model="tutorials.withdrawVideoLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Tutoriel retrait</label>
          <input
            class="inputComponent"
            v-model="tutorials.withdrawTutorialLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vidéo recharge</label>
          <input class="inputComponent" v-model="tutorials.rechargeVideoLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Tutoriel recharge</label>
          <input
            class="inputComponent"
            v-model="tutorials.rechargeTutorialLink"
          />
        </div>
      </div>
    </section>

    <!-- ASSISTANCE -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Assistance</h3>
        <button
          v-if="sectionChanged(assistance)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(assistance)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">WhatsApp réclamations</label>
          <input
            class="inputComponent"
            v-model="assistance.whatsappComplaintLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">WhatsApp assistance</label>
          <input
            class="inputComponent"
            v-model="assistance.whatsappAssistanceLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Telegram assistance</label>
          <input
            class="inputComponent"
            v-model="assistance.telegramAssistanceLink"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Email</label>
          <input class="inputComponent" v-model="assistance.email" />
        </div>
      </div>
    </section>

    <!-- OTHER LINKS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">Autres liens</h3>
        <button
          v-if="sectionChanged(otherLinks)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(otherLinks)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Snapchat</label>
          <input class="inputComponent" v-model="otherLinks.snapchatLink" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Reset mot de passe</label>
          <input class="inputComponent" v-model="otherLinks.passwordResetUrl" />
        </div>
        <div class="md:col-span-2">
          <label class="block mb-1 text-sm">Texte affiliation</label>
          <textarea
            class="inputComponent h-20 resize-none"
            v-model="otherLinks.affiliationDetailText"
          ></textarea>
        </div>
      </div>
    </section>

    <!-- DOWNLOAD URLS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          Liens de téléchargement
        </h3>
        <button
          v-if="sectionChanged(downloads)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(downloads)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm">Netflix</label>
          <input
            class="inputComponent"
            v-model="downloads.netflixDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Crunchyroll</label>
          <input
            class="inputComponent"
            v-model="downloads.crunchyrolDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">1xBet</label>
          <input class="inputComponent" v-model="downloads.xbetDownloadUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Betwinner</label>
          <input
            class="inputComponent"
            v-model="downloads.betwinnerDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">1Win</label>
          <input class="inputComponent" v-model="downloads.oneWinDownloadUrl" />
        </div>
        <div>
          <label class="block mb-1 text-sm">Prime Video</label>
          <input
            class="inputComponent"
            v-model="downloads.primeVideoDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Spotify</label>
          <input
            class="inputComponent"
            v-model="downloads.spotifyDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Nord VPN</label>
          <input
            class="inputComponent"
            v-model="downloads.nordVPNDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Private VPN</label>
          <input
            class="inputComponent"
            v-model="downloads.privateVPNDownloadUrl"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">CyberGhost</label>
          <input
            class="inputComponent"
            v-model="downloads.cyberGhostDownloadUrl"
          />
        </div>
      </div>
    </section>

    <!-- MIN AMOUNTS -->
    <section class="bg-slate-100 rounded-3xl p-6 space-y-4 relative">
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          Montants minimums
        </h3>
        <button
          v-if="sectionChanged(minAmounts)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetSection(minAmounts)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label class="block mb-1 text-sm">Transaction générale</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountForTransaction"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Alimentation wallet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountFundWallet"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Retrait wallet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountwithdrawalWallet"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat 1xBet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyXbet"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente 1xBet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellXbet"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat Betwinner</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyBetwinner"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente Betwinner</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellBetwinner"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat 1Win</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyOneWin"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente 1Win</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellOneWin"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat PM</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyPm"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente PM</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellPm"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat USDT</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyUsdt"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente USDT</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellUsdt"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat 8 Starz</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyEightStarz"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente 8 Starz</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellEightStarz"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Achat Melbet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountBuyMelbet"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm">Vente Melbet</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="minAmounts.minAmountSellMelbet"
          />
        </div>
      </div>
    </section>

  </div>
</template>
